import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { Buildings, Storefront, ArrowLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import useAuthStore from '@stores/authStore';

export default function ContextBar({ height = 40 }) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();

  const venueId = useAuthStore((s) => s.venueId);
  const venueName = useAuthStore((s) => s.venueName);
  const restaurantId = useAuthStore((s) => s.restaurantId);
  const restaurantName = useAuthStore((s) => s.restaurantName);
  const returnToVenue = useAuthStore((s) => s.returnToVenue);

  const handleBackToVenue = useCallback(() => {
    const origin = returnToVenue();
    navigate(origin ?? `/venue/${venueId}/restaurants`);
  }, [returnToVenue, navigate, venueId]);

  if (!venueId) return null;

  const isRestaurantMode = !!restaurantId;

  return (
    <Box
      aria-label={isRestaurantMode ? 'Restaurant context' : 'Venue context'}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height,
        zIndex: theme.zIndex.appBar + 10,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1,
      }}
    >
      {isRestaurantMode ? (
        <>
          <Storefront size={15} weight="fill" style={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
          <Typography variant="caption" fontWeight={600} sx={{ flex: 1, color: 'text.primary' }} noWrap>
            {restaurantName ?? restaurantId}
          </Typography>
          <Button
            size="small"
            startIcon={<ArrowLeft size={13} />}
            onClick={handleBackToVenue}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '0.75rem',
              minWidth: 0,
              px: 1,
              py: 0,
              '&:hover': { color: 'text.primary' },
            }}
          >
            {t('nav.backToVenue', 'Zurück zur Venue')}
          </Button>
        </>
      ) : (
        <>
          <Buildings size={15} weight="fill" style={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
          <Typography variant="caption" fontWeight={600} sx={{ color: 'text.primary' }} noWrap>
            {venueName ?? venueId}
          </Typography>
        </>
      )}
    </Box>
  );
}
